import { array, mixed, object, string } from "yup";

const WAGE_ROW_FIELDS = [
  "nomenclature",
  "wageItemType",
  "irrf",
  "inss",
  "fgts",
  "averages",
  "incorporateSalary",
];

const isCompleteWageRow = row =>
  row &&
  typeof row === "object" &&
  WAGE_ROW_FIELDS.every(field => {
    const value = row[field];
    return value !== undefined && value !== null && String(value).trim() !== "";
  });

const wageRowsSchema = message =>
  mixed().test("complete-wage-rows", message, value => {
    const rows = Object.values(value || {}).filter(
      row => row && typeof row === "object" && !Array.isArray(row),
    );
    return rows.length > 0 && rows.every(isCompleteWageRow);
  });

export const businessRuleSchema = object().shape({
  businessRuleIsValidated: string().required("Validado é obrigatório(a)"),

  collectiveAgreementType: string().required("Tipo de acordo coletivo é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para tipo de acordo coletivo"),

  applyCollectiveAgreementRules: string().required("Aplicação das regras do acordo é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para aplicação das regras do acordo"),
  collectiveAgreementPartialClauses: string().when("applyCollectiveAgreementRules", {
    is: "2",
    then: string().required("Cláusulas não seguidas do acordo é obrigatório(a)"),
    otherwise: string().optional().nullable(),
  }),
  applyCollectiveAgreementRulesOther: string().when("applyCollectiveAgreementRules", {
    is: "4",
    then: string().required("Descreva a regra de negócio"),
    otherwise: string().optional().nullable(),
  }),

  vacationAdvanceDiscounts: string().required("Descontos antecipados nas férias é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para descontos antecipados nas férias"),
  vacationAdvanceDiscountsList: string().when("vacationAdvanceDiscounts", {
    is: "1",
    then: string().required("Sinalize os descontos antecipados"),
    otherwise: string().optional().nullable(),
  }),

  vacationDiscountConditions: string().when("vacationAdvanceDiscounts", {
    is: "1",
    then: string().required("Condições para aplicar o desconto é obrigatório(a)"),
    otherwise: string().optional().nullable(),
  }),

  vacationAverages: string().required("Médias de férias é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para médias de férias"),
  vacationAveragesOther: string().when("vacationAverages", {
    is: "3",
    then: string().required("Informe as médias"),
    otherwise: string().optional().nullable(),
  }),

  vacationBenefits: string().required("Benefícios nas férias é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para benefícios nas férias"),
  vacationBenefitsList: string().when("vacationBenefits", {
    is: "1",
    then: string().required("Benefícios concedidos nas férias é obrigatório(a)"),
    otherwise: string().optional().nullable(),
  }),
  vacationBenefitsDescription: string().when("vacationBenefits", {
    is: "4",
    then: string().required("Descreva a regra de negócio"),
    otherwise: string().optional().nullable(),
  }),

  terminationDiscounts: array().required("Descontos na rescisão é obrigatório"),
  terminationDiscountsOther: string().when("terminationDiscounts", {
    is: val => Array.isArray(val) && val.some(o => o.value === "4"),
    then: string().required("Especifique os descontos"),
    otherwise: string().optional().nullable(),
  }),

  loanDiscountPercentageTermination: string().required("Percentual de desconto do empréstimo na rescisão é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para percentual de desconto do empréstimo na rescisão"),
  loanDiscountPercentageTerminationOther: string().when("loanDiscountPercentageTermination", {
    is: "3",
    then: string().required("Especifique o percentual"),
    otherwise: string().optional().nullable(),
  }),

  terminationAverages: string().required("Médias na rescisão é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para médias na rescisão"),
  terminationAveragesOther: string().when("terminationAverages", {
    is: "3",
    then: string().required("Informe as médias"),
    otherwise: string().optional().nullable(),
  }),

  payrollHasInterns: string().required("Estagiários na folha é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para estagiários na folha"),
  internSalaryAdjustmentRule: string().when("payrollHasInterns", {
    is: "1",
    then: string().required("Reajuste salarial para estagiários é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para reajuste salarial para estagiários"),
    otherwise: string().optional().nullable(),
  }),
  internSalaryAdjustmentOther: string().when(
    ["payrollHasInterns", "internSalaryAdjustmentRule"],
    {
      is: (payrollHasInterns, internSalaryAdjustmentRule) =>
        payrollHasInterns === "1" && internSalaryAdjustmentRule === "3",
      then: string().required("Descreva a regra de negócio"),
      otherwise: string().optional().nullable(),
    },
  ),
  internHasDeductions: string().when("payrollHasInterns", {
    is: "1",
    then: string().required("Descontos para estagiários é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para descontos para estagiários"),
    otherwise: string().optional().nullable(),
  }),
  internDeductionsDescription: string().when(
    ["payrollHasInterns", "internHasDeductions"],
    {
      is: (payrollHasInterns, internHasDeductions) =>
        payrollHasInterns === "1" && internHasDeductions === "1",
      then: string().required("Descreva verbas | rubricas"),
      otherwise: string().optional().nullable(),
    },
  ),
  internHasBenefits: string().when("payrollHasInterns", {
    is: "1",
    then: string().required("Benefícios para estagiários é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para benefícios para estagiários"),
    otherwise: string().optional().nullable(),
  }),
  interBenefitsCalculation: string().when(["payrollHasInterns", "internHasBenefits"], {
    is: (payrollHasInterns, internHasBenefits) =>
      payrollHasInterns === "1" && internHasBenefits === "1",
    then: string().required("Cálculo de benefícios para estagiário é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para cálculo de benefícios para estagiário"),
    otherwise: string().optional().nullable(),
  }),
  interPayrollProcessing: string().when(["payrollHasInterns", "internHasBenefits"], {
    is: (payrollHasInterns, internHasBenefits) =>
      payrollHasInterns === "1" && internHasBenefits === "1",
    then: string().required("Processamento de folha para estagiário é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para processamento de folha para estagiário"),
    otherwise: string().optional().nullable(),
  }),
  internFixedBenefit: string().when(["payrollHasInterns", "internHasBenefits"], {
    is: (payrollHasInterns, internHasBenefits) =>
      payrollHasInterns === "1" && internHasBenefits === "1",
    then: string().required("Benefício fixo para estagiários é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para benefício fixo para estagiários"),
    otherwise: string().optional().nullable(),
  }),
  internFixedBenefitRubric: string().when(
    ["payrollHasInterns", "internHasBenefits", "internFixedBenefit"],
    {
      is: (payrollHasInterns, internHasBenefits, internFixedBenefit) =>
        payrollHasInterns === "1" && internHasBenefits === "1" && internFixedBenefit === "1",
      then: string().required("Descreva verbas | rubricas"),
      otherwise: string().optional().nullable(),
    },
  ),
  internFixedBenefitRule: string().when(
    ["payrollHasInterns", "internHasBenefits", "internFixedBenefit"],
    {
      is: (payrollHasInterns, internHasBenefits, internFixedBenefit) =>
        payrollHasInterns === "1" && internHasBenefits === "1" && internFixedBenefit === "2",
      then: string().required("Descreva a regra de negócio"),
      otherwise: string().optional().nullable(),
    },
  ),
  internBenefitValue: string().when(["payrollHasInterns", "internHasBenefits"], {
    is: (payrollHasInterns, internHasBenefits) =>
      payrollHasInterns === "1" && internHasBenefits === "1",
    then: string().required("Valor do benefício para estagiário é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para valor do benefício para estagiário"),
    otherwise: string().optional().nullable(),
  }),
  internProportionalCalcRule: string().when(["payrollHasInterns", "internHasBenefits"], {
    is: (payrollHasInterns, internHasBenefits) =>
      payrollHasInterns === "1" && internHasBenefits === "1",
    then: string().required("Cálculo proporcional para estagiários é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para cálculo proporcional para estagiários"),
    otherwise: string().optional().nullable(),
  }),
  internProportionalCalcRuleOther: string().when(
    ["payrollHasInterns", "internHasBenefits", "internProportionalCalcRule"],
    {
      is: (payrollHasInterns, internHasBenefits, internProportionalCalcRule) =>
        payrollHasInterns === "1" && internHasBenefits === "1" && internProportionalCalcRule === "4",
      then: string().required("Informe o divisor"),
      otherwise: string().optional().nullable(),
    },
  ),

  payrollHasApprentices: string().required("Aprendizes na folha é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para aprendizes na folha"),
  apprenticeSalaryAdjustmentRule: string().when("payrollHasApprentices", {
    is: "1",
    then: string().required("Reajuste salarial para aprendizes é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para reajuste salarial para aprendizes"),
    otherwise: string().optional().nullable(),
  }),
  apprenticeSalaryAdjustmentOther: string().when(
    ["payrollHasApprentices", "apprenticeSalaryAdjustmentRule"],
    {
      is: (payrollHasApprentices, apprenticeSalaryAdjustmentRule) =>
        payrollHasApprentices === "1" && apprenticeSalaryAdjustmentRule === "3",
      then: string().required("Descreva a regra de negócio"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeHasDeductions: string().when("payrollHasApprentices", {
    is: "1",
    then: string().required("Possui descontos para aprendiz é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui descontos para aprendiz"),
    otherwise: string().optional().nullable(),
  }),
  apprenticeDeductionsDescription: string().when(
    ["payrollHasApprentices", "apprenticeHasDeductions"],
    {
      is: (payrollHasApprentices, apprenticeHasDeductions) =>
        payrollHasApprentices === "1" && apprenticeHasDeductions === "1",
      then: string().required("Descreva verbas | rubricas"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeHasBenefits: string().when("payrollHasApprentices", {
    is: "1",
    then: string().required("Possui benefícios para aprendiz é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui benefícios para aprendiz"),
    otherwise: string().optional().nullable(),
  }),
  apprenticeBenefitsCalculation: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1",
      then: string().required("Cálculo de benefícios para aprendiz é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para cálculo de benefícios para aprendiz"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticePayrollProcessing: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1",
      then: string().required("Processamento de folha para aprendiz é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para processamento de folha para aprendiz"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeFixedBenefit: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1",
      then: string().required("Benefício fixo para aprendiz é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para benefício fixo para aprendiz"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeFixedBenefitRubric: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits", "apprenticeFixedBenefit"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits, apprenticeFixedBenefit) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1" && apprenticeFixedBenefit === "1",
      then: string().required("Descreva verbas | rubricas"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeFixedBenefitRule: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits", "apprenticeFixedBenefit"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits, apprenticeFixedBenefit) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1" && apprenticeFixedBenefit === "2",
      then: string().required("Descreva a regra de negócio"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeBenefitValue: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1",
      then: string().required("Valor do benefício para aprendiz é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para valor do benefício para aprendiz"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeProportionalCalcRule: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1",
      then: string().required("Cálculo proporcional para aprendiz é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para cálculo proporcional para aprendiz"),
      otherwise: string().optional().nullable(),
    },
  ),
  apprenticeProportionalCalcRuleOther: string().when(
    ["payrollHasApprentices", "apprenticeHasBenefits", "apprenticeProportionalCalcRule"],
    {
      is: (payrollHasApprentices, apprenticeHasBenefits, apprenticeProportionalCalcRule) =>
        payrollHasApprentices === "1" && apprenticeHasBenefits === "1" && apprenticeProportionalCalcRule === "4",
      then: string().required("Informe o divisor"),
      otherwise: string().optional().nullable(),
    },
  ),

  hasAutonomous: string().required("Possui autônomos é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui autônomos"),
  autonomousBusinessRule: string().when("hasAutonomous", {
    is: "1",
    then: string().required("Descreva a regra de negócio"),
    otherwise: string().optional().nullable(),
  }),

  hasProLabore: string().required("Possui pró-labore é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui pró-labore"),
  proLaboreSalaryAdjustmentRule: string().when("hasProLabore", {
    is: "1",
    then: string().required("Reajuste salarial para pró-labore é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para reajuste salarial para pró-labore"),
    otherwise: string().optional().nullable(),
  }),
  proLaboreSalaryAdjustmentOther: string().when("proLaboreSalaryAdjustmentRule", {
    is: "3",
    then: string().required("Descreva a regra de negócio"),
    otherwise: string().optional().nullable(),
  }),
  proLaboreHasFgtsNoLink: string().when("hasProLabore", {
    is: "1",
    then: string().required("Pro labore com FGTS e sem vínculo é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para pro labore com fgts e sem vínculo"),
    otherwise: string().optional().nullable(),
  }),

  payrollAnalyticalReportOrder: string().required("Ordem do relatório analítico é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para ordem do relatório analítico"),
  payrollSyntheticReportOrder: string().required("Ordem do relatório sintético é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para ordem do relatório sintético"),
  payrollNetReport: string().required("Relatório líquido é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para relatório líquido"),
  payrollNetReportExcel: string().required("Relatório líquido excel é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para relatório líquido excel"),
  payrollAccountingSyntheticReportOrder: string().required("Ordem do relatório sintético contábil é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para ordem do relatório sintético contábil"),

  hasDomesticEmployee: string().required("Possui empregado doméstico é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui empregado doméstico"),
  domesticEmployeeSalaryAdjustmentRule: string().when("hasDomesticEmployee", {
    is: "1",
    then: string().required("Reajuste salarial para empregado doméstico é obrigatório").oneOf(["1", "2", "3"], "Selecione uma opção válida para reajuste salarial para empregado doméstico"),
    otherwise: string().optional().nullable(),
  }),
  domesticEmployeeSalaryAdjustmentRuleOther: string().when(
    "domesticEmployeeSalaryAdjustmentRule",
    {
      is: "3",
      then: string().required("Descreva a regra de negócio"),
      otherwise: string().optional().nullable(),
    },
  ),
  domesticEmployeeWorkTimeControl: string().when("hasDomesticEmployee", {
    is: "1",
    then: string().required("Controle de ponto de empregado doméstico é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para controle de ponto de empregado doméstico"),
    otherwise: string().optional().nullable(),
  }),
  domesticEmployeeClosePeriod: string().when("hasDomesticEmployee", {
    is: "1",
    then: string().required("Período de fechamento de empregado doméstico é obrigatório").oneOf(["1", "2", "3", "4"], "Selecione uma opção válida para período de fechamento de empregado doméstico"),
    otherwise: string().optional().nullable(),
  }),
  domesticEmployeeClosePeriodOther: string().when("domesticEmployeeClosePeriod", {
    is: "4",
    then: string().required("Descreva a regra de negócio"),
    otherwise: string().optional().nullable(),
  }),

  hasSpecificWageItems: string().required("Possui rubricas salariais específicas é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui rubricas salariais específicas"),
  specificWageRows: mixed().when("hasSpecificWageItems", {
    is: "1",
    then: wageRowsSchema("Preencha todas as células da linha de rubricas salariais"),
    otherwise: mixed().optional().nullable(),
  }),
  hasCustomWageIncidences: string().required("Possui incidências salariais customizadas é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui incidências salariais customizadas"),
  customWageRows: mixed().when("hasCustomWageIncidences", {
    is: "1",
    then: wageRowsSchema("Preencha todas as células da linha de incidências salariais"),
    otherwise: mixed().optional().nullable(),
  }),

  hasHazardPay: string().required("Possui periculosidade é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui periculosidade"),
  hasInsalubrityPay: string().required("Possui insalubridade é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui insalubridade"),
  discountAdditionalOnAbsence: string().when(["hasHazardPay", "hasInsalubrityPay"], {
    is: (hasHazardPay, hasInsalubrityPay) => hasHazardPay === "1" || hasInsalubrityPay === "1",
    then: string().required("Descontar adicional nas faltas é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para descontar adicional nas faltas"),
    otherwise: string().optional().nullable(),
  }),
  proportionalRuleForDifferentMonthDays: string().required("Regra proporcional para dias exatos do mês é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para regra proporcional para dias exatos do mês"),

  otherExclusiveRules: string().required("Possui outras regras exclusivas é obrigatório").oneOf(["1", "2"], "Selecione uma opção válida para possui outras regras exclusivas"),
  otherExclusiveRulesDescription: string().when("otherExclusiveRules", {
    is: "1",
    then: string().required("Descreva a regra de negócio"),
    otherwise: string().optional().nullable(),
  }),
});
